import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Displays volume pricing table with expandable rows.
 * Shows pricing tiers based on quantity thresholds.
 *
 * Frelan (2026-10-08): each tier row, "1+" included, is a button that SETS THE QUANTITY
 * BOX to that tier (snippets/volume-pricing-row.liquid) — press 25+ and the box reads 25,
 * whatever it held before. Nothing goes in the cart until Add to cart / Backorder is
 * pressed; that button's "· £x/ea" follows the new quantity (assets/price-per-item.js).
 * The marked row (aria-pressed) is the price they will actually pay: the tier reached by
 * what is already in the order PLUS the quantity box — the same sum as the button's
 * "· £x/ea". So with 25 in the order, 25+ stays marked even when the box says 1, and the
 * "25 already in your order" line under the table says why. Kept in step with typing, the
 * +/- buttons, cart changes and finish changes. (Until 2026-10-08 a row added the tier
 * straight to the cart.)
 *
 * @extends {Component}
 */
class VolumePricingComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    // A finish change morphs this table from the new page (back to its server-rendered
    // marker), after the quantity box has kept its value — so re-mark once it settles.
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    // The in-order count lives on the quantity input (data-cart-quantity), set by
    // product-form.js after every cart change — watch it rather than racing the cart events.
    this.#watchCartQuantity();
    this.#syncActive();
  }

  /** @type {MutationObserver | undefined} */
  #cartQuantityObserver;

  /** The product form's quantity input. */
  get #quantityInput() {
    return /** @type {HTMLInputElement | null | undefined} */ (
      this.closest('form')?.querySelector('input[name="quantity"]')
    );
  }

  #watchCartQuantity() {
    const input = this.#quantityInput;
    if (!input) return;
    this.#cartQuantityObserver?.disconnect();
    this.#cartQuantityObserver = new MutationObserver(() => this.#syncActive());
    this.#cartQuantityObserver.observe(input, { attributes: true, attributeFilter: ['data-cart-quantity'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#cartQuantityObserver?.disconnect();
  }

  /** The product form's quantity selector, if it has one. */
  get #quantitySelector() {
    return /** @type {any} */ (this.closest('form')?.querySelector('quantity-selector-component') ?? null);
  }

  /** @param {Event} event */
  #onQuantityUpdate = (event) => {
    const form = this.closest('form');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;
    this.#syncActive();
  };

  #onVariantUpdate = () => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.#watchCartQuantity();
        this.#syncActive();
      })
    );
  };

  /**
   * Toggles the expanded state of the volume pricing table
   */
  toggleExpanded() {
    this.classList.toggle('volume-pricing--expanded');
  }

  /**
   * Sets the quantity box to the pressed tier, clamped to what the box allows (minimum,
   * and any maximum left after what is already in the cart).
   * @param {MouseEvent} event
   */
  selectTier(event) {
    const row = event.target instanceof Element ? event.target.closest('button') : null;
    const selector = this.#quantitySelector;
    if (!(row instanceof HTMLButtonElement) || row.disabled || !selector) return;

    const tier = Number(row.dataset.quantity);
    if (!tier) return;

    const { min } = selector.getCurrentValues();
    const max = selector.getEffectiveMax();
    const quantity = Math.min(max ?? Infinity, Math.max(min, tier));

    selector.setValue(String(quantity));
    // Same path as typing a number: broadcasts the change (button price, this table)
    // and refreshes the +/- buttons.
    selector.onQuantityChange();
    selector.updateButtonStates();
    this.#syncActive();
  }

  /** Marks the highest tier reached by what is in the order plus the box, and updates the
   * "already in your order" line. */
  #syncActive() {
    const input = this.#quantityInput;
    const inOrder = parseInt(input?.getAttribute('data-cart-quantity') ?? '') || 0;
    const quantity = inOrder + (parseInt(input?.value ?? '') || 1);

    const line = /** @type {HTMLElement | null} */ (this.querySelector('.volume-pricing__in-order'));
    if (line) {
      line.hidden = inOrder <= 0;
      if (inOrder > 0 && line.dataset.template) {
        line.textContent = line.dataset.template.replace('[count]', String(inOrder));
      }
    }

    const rows = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('button.volume-pricing__row--action')]);
    let active = null;
    for (const row of rows) {
      const tier = Number(row.dataset.quantity);
      if (tier <= quantity && (!active || tier > Number(active.dataset.quantity))) active = row;
    }
    for (const row of rows) row.setAttribute('aria-pressed', String(row === active));
  }
}

if (!customElements.get('volume-pricing')) {
  customElements.define('volume-pricing', VolumePricingComponent);
}
